import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  useGoOfflineMutation,
  useHeartbeatMutation,
} from '@/services/socialApi';
import type {
  ContextRef,
  HeartbeatInput,
  PresenceStatus,
} from '@/types/social';
import { useAppSelector } from '@/store/hooks';

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Derive the current Mini-App context (tournament / match) from the active
 * URL. Mirrors the routes in `src/routes/router.tsx`:
 *   /tournaments/:id        → { type: 'tournament', id }
 *   /tournaments/:id/...    → { type: 'tournament', id }
 *   /matches/:id            → { type: 'match', id }
 *
 * Wave-1 we only mark a couple of pages — the heartbeat itself works
 * everywhere; the context is just an enrichment so the server's online-feed
 * can show "Mario is watching tournament X". Other routes send no context.
 */
function deriveContextFromPath(pathname: string): ContextRef | null {
  const tournamentMatch = pathname.match(/^\/tournaments\/(\d+)/);
  if (tournamentMatch) {
    return { type: 'tournament', id: tournamentMatch[1] };
  }
  const matchMatch = pathname.match(/^\/matches\/(\d+)/);
  if (matchMatch) {
    return { type: 'match', id: matchMatch[1] };
  }
  return null;
}

function deriveStatusFromContext(ctx: ContextRef | null): PresenceStatus {
  if (!ctx) return 'online';
  // We don't yet know if the user is registered/active vs just spectating —
  // "watching" is the safer default and matches the QSocials watch flow
  // (CoWatchOverlay also calls joinWatch which is presence-orthogonal).
  return 'watching';
}

/**
 * Posts a presence heartbeat to qsocials every 30s while the app is in
 * focus, including a context ref (`tournament:<id>` / `match:<id>`) when
 * the user is on a relevant page. Sends `/offline` on tab blur/unload.
 *
 * Mount this hook once at the AppShell layer (after auth).
 */
export function usePresenceHeartbeat(): void {
  const isAuthenticated = useAppSelector(
    (s) => s.auth.bridgeAuthenticated,
  );
  const { pathname } = useLocation();
  // Strip the trailing route params for the context derivation (`useParams`
  // is only safe inside a matched route — we want the raw path).
  // useParams call kept commented for reference; deriveContextFromPath
  // gives us all we need.
  void useParams;

  const [postHeartbeat] = useHeartbeatMutation();
  const [postOffline] = useGoOfflineMutation();

  const latestContextRef = useRef<ContextRef | null>(null);

  useEffect(() => {
    latestContextRef.current = deriveContextFromPath(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    const send = () => {
      if (cancelled) return;
      const ctx = latestContextRef.current;
      const payload: HeartbeatInput = {
        status: deriveStatusFromContext(ctx),
        surface: 'telegram',
        context: ctx,
      };
      // Fire-and-forget — RTK Query handles auth retry; we don't want to
      // surface heartbeat errors to the UI.
      postHeartbeat(payload)
        .unwrap()
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.debug('[presence] heartbeat failed:', err);
          }
        });
    };

    // Initial beat + interval.
    send();
    const interval = window.setInterval(send, HEARTBEAT_INTERVAL_MS);

    // Pause while the page is hidden (Telegram WebView collapsed, tab
    // backgrounded). Resume on visibility.
    const onVisibility = () => {
      if (document.hidden) {
        postOffline().unwrap().catch(() => {});
      } else {
        send();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Best-effort offline beacon on unload. The fetch is async but most
    // mobile WebViews will fire it before tearing the page down.
    const onBeforeUnload = () => {
      postOffline().unwrap().catch(() => {});
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
      postOffline().unwrap().catch(() => {});
    };
  }, [isAuthenticated, postHeartbeat, postOffline]);
}
